/**
 * The manned-emplacement tell on screen (ROUND4 §3). A concrete bunker stops carbine fire, so the
 * player has to be told who is inside it BEFORE it opens up - and told just as plainly when it is
 * empty, so he can walk past one he has already cleared.
 *
 * Who owns the barrel: `StaticWorld` does. The gun has to turn every frame with the gunner's
 * `facing`, and `syncPropStates` is already the once-a-frame pass that reads the world for the
 * props; the prop also knows its own seat and yaw, and takes the gun and the light down with it
 * when it is destroyed. `Actors` never sees the prop, so it could not place either part.
 *
 * The gun is deliberately NOT part of `buildPropModel`: no crew, no barrel.
 */

import { type BufferGeometry, Group, type Material, Mesh, type Object3D, type Scene, type Texture } from 'three';
import { Pcg32 } from '../../sim/rng';
import type { Enemy, PropDef, World } from '../../sim/types';
import { EMPLACEMENT_TELLS, type EmplacementTell } from '../kit/emplacements';
import { createEmissiveMaterial } from '../materials/toon';
import type { Palette, TimeOfDay } from '../palette';
import { glowBoost } from './PropGlow';

const TAU = Math.PI * 2;
/** The barrel turns in notches, as every figure and the searchlight head do (05 §6.2). */
const TRAVERSE_STEPS = 32;

/** Signed difference between two headings, in (-PI, PI]. */
function angleDiff(from: number, to: number): number {
  let d = (to - from) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d < -Math.PI) d += TAU;
  return d;
}

interface View {
  root: Group;
  gun: Object3D | null;
  slot: Object3D;
  yaw: number;
  arc: number;
}

/** Alive and still fighting: a dead or bleeding-out gunner is no longer behind his slot. */
export function mansEmplacement(e: Enemy, propId: number): boolean {
  return e.mans === propId && e.state !== 'dead' && e.state !== 'wounded';
}

export class Emplacements {
  private readonly views = new Map<number, View>();
  private readonly geometries = new Map<string, BufferGeometry>();
  private readonly slotMaterial: Material;

  constructor(
    private readonly scene: Scene,
    private readonly palette: Palette,
    gradient: Texture,
    /** The props' own toon material: a gun in a wall is part of the structure, not a character. */
    private readonly material: Material,
    timeOfDay: TimeOfDay,
    props: readonly PropDef[],
    seatY: (prop: PropDef) => number,
  ) {
    this.slotMaterial = createEmissiveMaterial(gradient, glowBoost(timeOfDay));
    for (const prop of props) {
      const tell = EMPLACEMENT_TELLS[prop.kind];
      if (tell) this.add(prop, tell, seatY(prop));
    }
  }

  /** Every emplacement of a kind shares one gun and one lit slot. */
  private geometry(name: string, build: () => BufferGeometry): BufferGeometry {
    let g = this.geometries.get(name);
    if (!g) {
      g = build();
      this.geometries.set(name, g);
    }
    return g;
  }

  private add(prop: PropDef, tell: EmplacementTell, y: number): void {
    const scale = prop.scale ?? 1;
    const root = new Group();
    root.position.set(prop.x, y, prop.z);
    root.rotation.y = prop.yaw;
    root.scale.setScalar(scale);
    const rng = (): Pcg32 => new Pcg32(prop.id, 31);
    const slot = new Mesh(
      this.geometry(`${prop.kind}:slot`, () => tell.slot(this.palette, rng()).build()),
      this.slotMaterial,
    );
    slot.visible = false;
    root.add(slot);
    let gun: Mesh | null = null;
    if (tell.gun) {
      gun = new Mesh(this.geometry(`${prop.kind}:gun`, () => (tell.gun ?? tell.slot)(this.palette, rng()).build()),
        this.material);
      gun.castShadow = true;
      gun.receiveShadow = true;
      gun.position.set(tell.pivot[0], tell.pivot[1], tell.pivot[2]);
      gun.visible = false;
      root.add(gun);
    }
    this.scene.add(root);
    this.views.set(prop.id, { root, gun, slot, yaw: prop.yaw, arc: tell.arc });
  }

  /** The gun and the light for one frame: shown only while a live gunner mans the prop. */
  update(world: World): void {
    if (this.views.size === 0) return;
    for (const [id, v] of this.views) {
      const gunner = world.enemies.find((e) => mansEmplacement(e, id));
      v.slot.visible = gunner !== undefined;
      if (!v.gun) continue;
      v.gun.visible = gunner !== undefined;
      if (!gunner) continue;
      // `facing` is a world heading; the gun hangs under the prop's own yaw, so aim relative to it.
      // Snapped first, then held to the arc, so the barrel can never notch its way through a wall.
      const step = TAU / TRAVERSE_STEPS;
      const snapped = Math.round(angleDiff(v.yaw, gunner.facing) / step) * step;
      v.gun.rotation.y = Math.max(-v.arc, Math.min(v.arc, snapped));
    }
  }

  /** Whether prop `id` is showing its tell this frame (tests, debug overlay). */
  isManned(id: number): boolean {
    return this.views.get(id)?.slot.visible ?? false;
  }

  /** The turning gun of prop `id`, or null: no crew-served gun, or the emplacement is gone. */
  gunOf(id: number): Object3D | null {
    return this.views.get(id)?.gun ?? null;
  }

  /** The emplacement was destroyed: its gun and its light go with it, leaving the wreck bare. */
  remove(id: number): void {
    const v = this.views.get(id);
    if (!v) return;
    this.scene.remove(v.root);
    this.views.delete(id);
  }

  dispose(): void {
    for (const id of [...this.views.keys()]) this.remove(id);
    for (const g of this.geometries.values()) g.dispose();
    this.geometries.clear();
    this.slotMaterial.dispose();
  }
}
